import type React from 'react';
import { useState, useCallback, useEffect } from 'react';

import { UserReadFields, dataSchema } from '@ragu-components/ord-types';
import { useReactFlow } from '@xyflow/react';
import { EmailField, TextField, useTranslate } from 'react-admin';

import { flowchartLogger } from 'src/lib/logger';

import ResourceList from '../../components/ResourceList';
import SortableInstructionsList from '../../components/SortableInstructionsList';
import NodeControlForm from '../NodeControlForm';

import type { UserInstruction, CallflowNodeInstruction, NodeControlsProps } from '../../dataProvider/types';
import type { UserRead } from '@ragu-components/ord-types';

export interface UserType extends Record<string, unknown> {
  user: UserRead | null;
  instructions: UserInstruction[];
}

interface UserControlsProps extends NodeControlsProps {
  updateNodeData: (data: Record<string, unknown>) => void;
  data: UserType;
}

export default function UserControls({ updateNodeData, data }: UserControlsProps): React.ReactElement {
  const [user, setUser] = useState<UserRead | null>(data.user ?? null);
  const [instructions, setInstructions] = useState<UserInstruction[]>(data.instructions ?? []);
  const reactFlowInstance = useReactFlow();
  const translate = useTranslate();

  const removeEdgesByHandles = useCallback(
    (handles: string[]) => {
      if (handles.length === 0 || reactFlowInstance === undefined) {
        return;
      }
      const edges = reactFlowInstance.getEdges();
      const filteredEdges = edges.filter((edge) => {
        if (edge.sourceHandle === undefined || edge.sourceHandle === null) {
          return true;
        }
        return !handles.includes(edge.sourceHandle);
      });
      if (filteredEdges.length !== edges.length) {
        flowchartLogger.debug('UserControls: deleting edges for handles', { handles });
        reactFlowInstance.setEdges(filteredEdges);
      }
    },
    [reactFlowInstance]
  );

  const onUserDataUpdate = (event: React.FormEvent): void => {
    event.preventDefault();
    flowchartLogger.debug('UserControls: onUserDataUpdate', { user, instructions });
    if (user === undefined || user === null) {
      flowchartLogger.debug('UserControls: userId is undefined or empty. Update cancelled.');
      return;
    }
    updateNodeData({ user, instructions });
  };

  useEffect(() => {
    flowchartLogger.debug('UserControls: userId changed', { user, instructions });
    updateNodeData({ user, instructions });
  }, [user, updateNodeData, instructions]);

  const updateIfValid = useCallback((): void => {
    if (
      // Have to check for null to avoid TS errors
      user === undefined ||
      user === null ||
      instructions === null ||
      instructions.length === 0 ||
      instructions.find(
        (instruction) => instruction.match_exit_causes === null || instruction.match_exit_causes.length === 0
      ) !== undefined
    ) {
      flowchartLogger.debug('UserControls: invalid form data, update cancelled');
      return;
    }
    flowchartLogger.debug('UserControls: node data updated');
    updateNodeData({ user, instructions });
  }, [user, instructions, updateNodeData]);

  const handleInstructionDelete = useCallback(
    (handleId: string) => {
      flowchartLogger.debug('UserControls: removing instruction handle', { handleId });
      removeEdgesByHandles([handleId]);
      updateIfValid();
    },
    [removeEdgesByHandles, updateIfValid]
  );

  return (
    <NodeControlForm
      onSubmit={onUserDataUpdate}
      isDisabled={user === undefined || user === null}
      data={data}
      updateNodeData={updateNodeData}
    >
      <SortableInstructionsList
        inputs={instructions}
        onChange={setInstructions as React.Dispatch<React.SetStateAction<CallflowNodeInstruction[]>>}
        onDelete={handleInstructionDelete}
        matchExitCauses={dataSchema.CallflowUser.properties.connections.items.properties.match_exit_causes.items.enum.map(
          (choice) => choice
        )}
        presets={[
          {
            description: translate('callflows.components.sortableInstructionsList.presets.busy'),
            match_exit_causes: ['USER_BUSY', 'BUSY_EVERYWHERE', 'CALL_REJECTED', 'DECLINE'],
          },
        ]}
      />
      <ResourceList
        resource='users'
        selectedResourceId={user !== null ? user.id : null}
        onRowClick={(_id, _resource, record) => {
          setUser(record as UserRead);
          return false;
        }}
      >
        <TextField source={UserReadFields.username} />
        <TextField source={UserReadFields.firstname} />
        <TextField source={UserReadFields.lastname} />
        <EmailField source={UserReadFields.email} />
      </ResourceList>
    </NodeControlForm>
  );
}
